/**
 * EscalationInbox.tsx
 *
 * Displays the current user's urgent request inbox with ACK / In-Progress / Done actions.
 * Used in both Control Center and Consultant Dashboard contexts.
 *
 * Enterprise tone, zero emojis, ALL CAPS acronyms.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader2,
  MessageSquare,
  Play,
  ShieldAlert,
  Zap,
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';

interface EscalationInboxProps {
  accessToken: string;
  currentUser: any;
  /** Only show requests for a specific engagement (Control Center mode) */
  engagementId?: string;
  /** Compact mode hides full descriptions */
  compact?: boolean;
}

interface EscalationRequest {
  firmId: string;
  engagementId: string;
  requestId: string;
  title: string;
  description?: string;
  createdAt: string;
  createdBy: string;
  createdByName?: string;
  urgent: boolean;
  status: string;
  assignedTo: string;
  assignedToName?: string;
  dueAt: string;
  respondedAt?: string;
  responseType?: string;
  breachedAt?: string;
  configItemName?: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  open: { label: 'OPEN', color: 'text-amber-700', bgColor: 'bg-amber-50 border-amber-200' },
  ack: { label: 'ACKNOWLEDGED', color: 'text-blue-700', bgColor: 'bg-blue-50 border-blue-200' },
  'in-progress': { label: 'IN PROGRESS', color: 'text-indigo-700', bgColor: 'bg-indigo-50 border-indigo-200' },
  done: { label: 'DONE', color: 'text-emerald-700', bgColor: 'bg-emerald-50 border-emerald-200' },
  breached: { label: 'SLA BREACHED', color: 'text-red-700', bgColor: 'bg-red-50 border-red-200' },
};

function timeAgo(isoDate: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const hours = Math.floor(diffMs / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  if (hours > 24) return `${Math.floor(hours / 24)}d ago`;
  if (hours > 0) return `${hours}h ${minutes}m ago`;
  return `${minutes}m ago`;
}

function timeRemaining(dueDateIso: string): { text: string; urgent: boolean } {
  const diffMs = new Date(dueDateIso).getTime() - Date.now();
  if (diffMs <= 0) return { text: 'OVERDUE', urgent: true };
  const hours = Math.floor(diffMs / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  return {
    text: hours > 0 ? `${hours}h ${minutes}m remaining` : `${minutes}m remaining`,
    urgent: diffMs < 2 * 3600000, // < 2h
  };
}

export function EscalationInbox({
  accessToken,
  currentUser,
  engagementId,
  compact = false,
}: EscalationInboxProps) {
  const [items, setItems] = useState<EscalationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState<string | null>(null);

  const fetchInbox = useCallback(async () => {
    try {
      setLoading(true);
      let url: string;

      if (engagementId) {
        url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/escalation/requests/${engagementId}`;
      } else {
        url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/escalation/inbox`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        const list = data.inbox || data.requests || [];
        // Filter to only items assigned to current user if in inbox mode
        if (!engagementId) {
          setItems(list);
        } else {
          setItems(list);
        }
      }
    } catch (err) {
      console.error('[EscalationInbox] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [accessToken, engagementId]);

  useEffect(() => {
    fetchInbox();
    const interval = setInterval(fetchInbox, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchInbox]);

  const handleRespond = async (
    item: EscalationRequest,
    responseType: 'ack' | 'in-progress' | 'done',
  ) => {
    setResponding(item.requestId);
    try {
      const url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/escalation/requests/${item.engagementId}/${item.requestId}/respond`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ responseType }),
      });

      if (response.ok) {
        const labels: Record<string, string> = {
          ack: 'Acknowledged',
          'in-progress': 'Marked as In Progress',
          done: 'Marked as Done',
        };
        toast.success(labels[responseType]);
        fetchInbox();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || 'Failed to respond');
      }
    } catch (err) {
      console.error('[EscalationInbox] Respond error:', err);
      toast.error('Network error');
    } finally {
      setResponding(null);
    }
  };

  // Partition items
  const breached = items.filter((i) => i.status === 'breached');
  const urgentOpen = items.filter((i) => i.urgent && i.status === 'open');
  const inProgress = items.filter(
    (i) => i.status === 'ack' || i.status === 'in-progress',
  );
  const resolved = items.filter((i) => i.status === 'done');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-5 animate-spin text-slate-400" />
        <span className="ml-2 text-[13px] text-slate-500">Loading requests</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="border-slate-200 shadow-none">
        <CardContent className="py-8 text-center">
          <CheckCircle className="size-8 text-emerald-400 mx-auto mb-3" />
          <p className="text-[13px] text-slate-600 font-medium">No escalation requests</p>
          <p className="text-[11px] text-slate-400 mt-1">All clear -- no urgent items require attention</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* BREACHED section */}
      {breached.length > 0 && (
        <Card className="border-red-300 shadow-none">
          <CardHeader className="pb-0 pt-4 px-5">
            <CardTitle className="text-[14px] font-semibold text-red-800 flex items-center gap-2">
              <ShieldAlert className="size-4" />
              SLA BREACHED ({breached.length})
            </CardTitle>
            <CardDescription className="text-[11px] text-red-600">
              These requests exceeded the 4-hour response window
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-3 pb-4 px-5 space-y-2">
            {breached.map((item) => (
              <RequestRow
                key={item.requestId}
                item={item}
                compact={compact}
                responding={responding}
                onRespond={handleRespond}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* URGENT OPEN section */}
      {urgentOpen.length > 0 && (
        <Card className="border-amber-300 shadow-none">
          <CardHeader className="pb-0 pt-4 px-5">
            <CardTitle className="text-[14px] font-semibold text-amber-800 flex items-center gap-2">
              <Zap className="size-4" />
              URGENT -- NEEDS RESPONSE ({urgentOpen.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3 pb-4 px-5 space-y-2">
            {urgentOpen.map((item) => (
              <RequestRow
                key={item.requestId}
                item={item}
                compact={compact}
                responding={responding}
                onRespond={handleRespond}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* IN PROGRESS section */}
      {inProgress.length > 0 && (
        <Card className="border-slate-200 shadow-none">
          <CardHeader className="pb-0 pt-4 px-5">
            <CardTitle className="text-[14px] font-semibold text-slate-700 flex items-center gap-2">
              <Play className="size-4" />
              IN PROGRESS ({inProgress.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3 pb-4 px-5 space-y-2">
            {inProgress.map((item) => (
              <RequestRow
                key={item.requestId}
                item={item}
                compact={compact}
                responding={responding}
                onRespond={handleRespond}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* RESOLVED section (collapsed by default in compact mode) */}
      {!compact && resolved.length > 0 && (
        <Card className="border-slate-200 shadow-none">
          <CardHeader className="pb-0 pt-4 px-5">
            <CardTitle className="text-[14px] font-semibold text-emerald-700 flex items-center gap-2">
              <CheckCircle className="size-4" />
              RESOLVED ({resolved.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3 pb-4 px-5 space-y-2">
            {resolved.map((item) => (
              <RequestRow
                key={item.requestId}
                item={item}
                compact={compact}
                responding={responding}
                onRespond={handleRespond}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: single request row with action buttons
// ---------------------------------------------------------------------------

function RequestRow({
  item,
  compact,
  responding,
  onRespond,
}: {
  item: EscalationRequest;
  compact: boolean;
  responding: string | null;
  onRespond: (item: EscalationRequest, type: 'ack' | 'in-progress' | 'done') => void;
}) {
  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.open;
  const isResponding = responding === item.requestId;
  const showActions = item.status !== 'done';
  const remaining = item.dueAt ? timeRemaining(item.dueAt) : null;

  return (
    <div className={`p-3 rounded-lg border ${statusCfg.bgColor}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[13px] font-medium text-slate-800 leading-snug">
              {item.title}
            </p>
            {item.urgent && (
              <Badge variant="destructive" className="text-[9px] px-1.5 py-0">
                URGENT
              </Badge>
            )}
            <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${statusCfg.color}`}>
              {statusCfg.label}
            </Badge>
          </div>

          {!compact && item.description && (
            <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{item.description}</p>
          )}

          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <span className="text-[10px] text-slate-400">
              Created {timeAgo(item.createdAt)}
              {item.createdByName ? ` by ${item.createdByName}` : ''}
            </span>
            {item.configItemName && (
              <span className="text-[10px] text-slate-400">
                CI: {item.configItemName}
              </span>
            )}
            {remaining && item.status !== 'done' && item.status !== 'breached' && (
              <span className={`text-[10px] font-medium ${remaining.urgent ? 'text-red-600' : 'text-slate-500'}`}>
                {remaining.text}
              </span>
            )}
            {item.breachedAt && (
              <span className="text-[10px] font-medium text-red-600">
                Breached {timeAgo(item.breachedAt)}
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {showActions && (
          <div className="flex items-center gap-1.5 shrink-0">
            {item.status === 'open' && (
              <Button
                size="sm"
                variant="outline"
                className="text-[11px] h-7 px-2"
                disabled={isResponding}
                onClick={() => onRespond(item, 'ack')}
              >
                {isResponding ? <Loader2 className="size-3 animate-spin" /> : 'ACK'}
              </Button>
            )}
            {(item.status === 'open' || item.status === 'ack') && (
              <Button
                size="sm"
                variant="outline"
                className="text-[11px] h-7 px-2"
                disabled={isResponding}
                onClick={() => onRespond(item, 'in-progress')}
              >
                {isResponding ? <Loader2 className="size-3 animate-spin" /> : 'IN PROGRESS'}
              </Button>
            )}
            {item.status !== 'done' && (
              <Button
                size="sm"
                variant="default"
                className="text-[11px] h-7 px-2"
                disabled={isResponding}
                onClick={() => onRespond(item, 'done')}
              >
                {isResponding ? <Loader2 className="size-3 animate-spin" /> : 'DONE'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
